import { useState } from 'react';
import { useAuth } from '../../../app/providers';
import { useNavigate } from '@tanstack/react-router';
import { onboardingApi, type OrganizationCreateRequest } from '../api';

const OrganizationSetupPage = () => {
  const [formData, setFormData] = useState<OrganizationCreateRequest>({
    name: '',
    address: '',
    phoneNumber: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (
    field: keyof OrganizationCreateRequest,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await onboardingApi.createOrganization(formData);
      setIsLoading(false);
      console.log('organization created successfully');
      navigate({ to: '/onboarding/finalize' });
    } catch (err: any) {
      setError(err.message || 'Failed to create organization');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-4 md:mb-12">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
            Welcome {user?.name}!
          </h1>
          <p className="text-lg md:text-xl text-gray-600">
            Start managing your sober living community with ease.
          </p>
        </div>

        {/* Organization Setup Form */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-4 md:mb-8">
            Organization details
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            {/* Organization Name */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Organization name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Sober House"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5761EB] focus:border-transparent"
                required
              />
            </div>

            {/* Location */}
            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Location
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Flat # 1, St 2, XYZ town"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5761EB] focus:border-transparent"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-400">🎯</span>
                </div>
              </div>
            </div>

            {/* Organization Number */}
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Organization number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400">🇺🇸</span>
                </div>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    handleInputChange('phoneNumber', e.target.value)
                  }
                  placeholder="+1 023 123 4567"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5761EB] focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#5761EB] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#5761EB]/90 focus:outline-none focus:ring-2 focus:ring-[#5761EB] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default OrganizationSetupPage;
